import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import Swal from "sweetalert2";
import {
  AddedTypes,
  BudgetItemType,
  BudgetItemTypeEnum,
  initialPaymentSolicitation,
} from "../../../../types/budget/budget";
import { Box, Button, Divider } from "@mui/material";
import { toast } from "react-toastify";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import SupplierService from "../../../../services/supplier.service";
import { currencyFormatter } from "../../../../utils/currencyFormatter";
import { BudgetService } from "../../../../services/budget.service";
import { TabActive } from "../../../../types";
import { useProject } from "../../../../contexts/project.context";
import { sortItemsByCategory } from "../../../../utils/category-sort";
import ActionSelect from "../../Budget/RightSideBar/ActionSelect";
import { useServices } from "../../../../contexts/ServicesContext/projectservices.context";
import { dispatchAddService } from "../../../../utils/add-service-gesture";
import SupplierAutoComplete from "../SupplierAutoComplete";
import ServiceGroup from "../ServiceGroup";

type PropTypes = {
  supplierId: string;
  index: number;
  calcOption: string;
};

function findAndSumBySupplierId(data: any, supplierId: string) {
  const result = [];

  let totals = {
    taxes: 0,
    revenueTotals: 0,
    serviceTaxTotals: 0,
    tariffTotals: 0,
    totals: 0,
  };

  for (const key in data) {
    const array = data[key];

    if (Array.isArray(array)) {
      for (const item of array) {
        if (item.supplierId === supplierId && item.calcOption) {
          result.push(item);

          // Add up the nested totalInBRL fields if they exist
          totals.taxes += item.taxes?.totalInBRL || 0;
          totals.revenueTotals += item.revenueTotals?.totalInBRL || 0;
          totals.serviceTaxTotals += item.serviceTaxTotals?.totalInBRL || 0;
          totals.tariffTotals += item.tariffTotals?.totalInBRL || 0;
          totals.totals += item.totals?.totalInBRL || 0;
        }
      }
    }
  }

  return {
    matchedItems: result,
    sumTotals: totals,
  };
}
const ServiceSupplier: React.FC<PropTypes> = ({
  supplierId,
  index,
  calcOption,
}: PropTypes) => {
  // const {
  //   activeBudgetDispatch,
  //   activeBudget,
  //   activeCalcOption,
  //   versionInfo,
  //   updateBudget,
  //   isBudgetDisabled,
  // } = useBudget();

  const {
    activeServiceDispatch,
    activeService,
    activeCalcOption,
    updateService,
  } = useServices();

  // const [supplierIdCleaned, setSupplierIdCleaned] = useState<string>(``);

  // useEffect(() => {
  //   const supplierIdSplit = supplierId.split("-");
  //   if (supplierIdSplit.length > 1) {
  //     setSupplierIdCleaned(supplierIdSplit[0]);
  //   } else {
  //     setSupplierIdCleaned("");
  //   }
  // }, [supplierId]);

  const parsedSupplierId = useMemo(() => {
    const providerIdSplit = supplierId.split("-");
    if (providerIdSplit.length > 1) {
      return providerIdSplit[0];
    } else {
      return supplierId;
    }
  }, [supplierId]);

  const { tabActive } = useProject();
  const [accordionState, setAccordionState] = useState<Map<string, boolean>>(
    new Map()
  );
  const [baseValuesAccordionState, setBaseValuesAccordionState] = useState<
    Map<string, boolean>
  >(new Map());

  const currentService: any = activeService;

  const totalsSummary = useMemo(() => {
    // Determine which totals source to use based on versionInfo.status
    let totalsSource;

    totalsSource =
      currentService?.budgetValuesTotal?.byCalculationType?.[calcOption];
    const { sumTotals: totals } = findAndSumBySupplierId(
      totalsSource,
      supplierId
    );
    return [
      { title: "T. Tarifas", value: totals.tariffTotals },
      { title: "T. Taxas", value: totals.serviceTaxTotals },
      { title: "T. Receita", value: totals.revenueTotals },
      { title: "T. Imposto", value: totals.taxes },
      { title: "T. Geral", value: totals.totals },
    ];
  }, [currentService, activeCalcOption, calcOption, supplierId]);

  const { data: provider } = useQuery({
    queryKey: ["provider", parsedSupplierId],
    queryFn: () =>
      SupplierService.getProviders({
        id: parsedSupplierId,
      }),
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false, // Disable refetch on window focus
    refetchOnMount: false, // Disable refetch on component mount
    cacheTime: 1000 * 60 * 10, // Data stays in cache for 10 minutes
  });

  type Option = { label: string; value: string };

  // Função para gerar as opções
  function generateFilteredOptions(
    enumObj: typeof BudgetItemTypeEnum,
    itemTypeList: { [key in keyof typeof BudgetItemTypeEnum]?: boolean }
  ): Option[] {
    if (!itemTypeList) {
      return [];
    }
    const keys = Object.keys(enumObj) as (keyof typeof BudgetItemTypeEnum)[];
    const filtered = keys.filter((key) => itemTypeList[key] === true);
    const options = filtered.map((key) => ({
      label: enumObj[key],
      value: String(key),
      type: String(key),
    }));
    return sortItemsByCategory(options, "type").map(({ label, value }) => ({
      label,
      value,
    }));
  }

  function handleStateChange(value: any, name: any) {
    if (!value) return;
    activeServiceDispatch({
      type: "UPDATE_SUPPLIER_LIST",
      payload: {
        currentId: supplierId,
        newId: value,
        newName: name,
        calcOption: activeCalcOption,
      },
    });
  }

  function addOption(optionValue: string) {
    const hasItemAlready = activeService?.addedItemTypeList?.findIndex(
      (el) =>
        el.supplierId === supplierId &&
        el.type === optionValue &&
        el.calcOption === activeCalcOption
    );
    if (hasItemAlready > -1)
      return toast.warn("A opção selecionada já existe para esse fornecedor");
    if (!supplierId)
      return toast.warn("Selecione um fornecedor para adicionar serviços.");

    // Find the current count of items for this supplier and calcOption
    const currentItemsForSupplier =
      activeService?.addedItemTypeList?.filter(
        (el) =>
          el.supplierId === supplierId && el.calcOption === activeCalcOption
      ) || [];

    const newItemIndex = currentItemsForSupplier.length;

    activeServiceDispatch({
      type: "ADD_BUDGET_ITEM",
      payload: { type: optionValue, supplierId, calcOption: activeCalcOption },
    });

    dispatchAddService(
      activeServiceDispatch,
      {
        type: "ADD_SERVICE",
        payload: {
          supplierId,
          supplierName: provider?.[0]?.name || "",
          type: optionValue,
          calcOption: activeCalcOption,
        },
      },
      { holdUntilFlush: false }
    );

    // Set the newly added item's accordion to expanded and collapse all others
    setAccordionState(() => {
      const newState = new Map(); // Fresh state - all others collapsed
      newState.set(`panel${newItemIndex}`, true); // Only expand the new item
      return newState;
    });
  }
  const isExpanded = (panel: string) => {
    return accordionState.get(panel) || false;
  };

  const isBaseValueExpanded = (panel: string) => {
    return baseValuesAccordionState.get(panel) || false;
  };

  const handleChangeBaseValue = useCallback(
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setBaseValuesAccordionState((prevState) => {
        const newState = new Map(prevState);
        newState.set(panel, isExpanded);
        return newState;
      });
    },
    [supplierId, activeCalcOption]
  );

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setAccordionState((prevState) => {
        const newState = new Map(prevState);
        newState.set(panel, isExpanded);
        return newState;
      });
    };
  function renderActiveOptions() {
    return activeService?.addedItemTypeList?.map(
      (el: AddedTypes, index: number) => {
        const savedPaymentInfo =
          activeService?.paymentSolicitation?.find(
            (paymentInfo) =>
              paymentInfo.type === el.type &&
              paymentInfo.supplierId === supplierId &&
              paymentInfo.calcOption === activeCalcOption
          ) || initialPaymentSolicitation;
        const panel = "panel" + index;
        const baseValuePanel = "budgetPanel" + index;
        return el.supplierId === supplierId &&
          el.calcOption === activeCalcOption ? (
          <Box
            width={"100%"}
            key={`${el.type}-${el.calcOption}-${el.supplierId}`}
          >
            <ServiceGroup
              type={el.type}
              supplierId={supplierId}
              supplierName={provider?.[0]?.name || ""}
              paymentInfo={savedPaymentInfo}
              expanded={isExpanded(panel)}
              baseValueExpanded={isBaseValueExpanded(baseValuePanel)}
              onChange={handleChange}
              panel={panel}
              baseValuePanel={baseValuePanel}
              onRemoveItem={removeBudgetItem}
              index={index}
              onChangeBaseValue={handleChangeBaseValue}
            />
          </Box>
        ) : (
          <></>
        );
      }
    );
  }
  function removeBudgetItem(type: BudgetItemType) {
    Swal.fire({
      title: "Tem certeza?",
      text: "Ao apagar este item todos serviços relacionados também serão apagados!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Não, cancelar",
      confirmButtonText: "Sim, prossiga",
    }).then((result) => {
      if (result.isConfirmed) {
        activeService.addedItemTypeList =
          activeService.addedItemTypeList.filter(
            (el) =>
              !(
                el.supplierId === supplierId &&
                el.type === type &&
                el.calcOption === calcOption
              )
          );
        activeService.budgetServices = activeService.budgetServices.filter(
          (el) =>
            !(
              el.supplierId === supplierId &&
              el.type === type &&
              el.calcOption === calcOption
            )
        );
        activeService.baseValues = activeService.baseValues.filter(
          (el) =>
            !(
              el.supplierId === supplierId &&
              el.category === type &&
              el.calcOption === calcOption &&
              el.version === 0
            )
        );
        if (activeService._id)
          updateService(activeService._id, activeService, "SAVE", -1);
        activeServiceDispatch({
          type: "DELETE_BUDGET_ITEM",
          payload: {
            type,
            supplierId,
            calcOption: activeCalcOption,
          },
          versionInfo: { version: 0, comparisonVersion: 0, status: "OPEN" },
        });
        Swal.fire({
          title: "Apagado",
          text: "O item foi apagado",
          icon: "success",
        });
      }
    });
  }
  function removeSupplier() {
    Swal.fire({
      title: "Tem certeza?",
      text: "Ao apagar este fornecedor todos os dados relacionados também serão apagados!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      cancelButtonText: "Não, cancelar",
      confirmButtonText: "Sim, prossiga",
    }).then((result) => {
      if (result.isConfirmed) {
        activeServiceDispatch({
          type: "DELETE_SUPPLIER",
          payload: {
            supplierId,
            calcOption: activeCalcOption,
          },
          versionInfo: { version: 0, comparisonVersion: 0, status: "OPEN" },
        });
        Swal.fire({
          title: "Apagado",
          text: "O item foi apagado",
          icon: "success",
        });
      }
    });
  }
  return (
    <Box
      sx={{
        background: "#E9E9EA",
        borderRadius: "8px",
        padding: "15px",
        display: "flex",
        flexDirection: "column",
        margin: "auto",
        width: "99%",
        marginTop: "5px",
        position: "relative",
      }}
    >
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        bgcolor="#FFF"
        p={1}
        borderRadius={2}
      >
        <Box
          display={"flex"}
          flex={1}
          margin={"auto"}
          alignItems={"center"}
          gap={1}
        >
          <Box gap={1} mt={1}>
            <span>{index + 1}</span>
          </Box>
          <SupplierAutoComplete
            onSelectSupplier={handleStateChange}
            provider_id={parsedSupplierId}
            title={``}
            disabled={false}
            isBudget={true}
          />
        </Box>
        <Box
          style={{ display: "flex", flexDirection: "row" }}
          flex={1}
          height={"100%"}
          justifyContent={"flex-end"}
          gap={1}
        >
          <Divider orientation="vertical" flexItem sx={{ mt: 1 }} />

          <Box
            display="flex"
            flex={0.9}
            justifyContent={"space-evenly"}
            gap={1}
            mt={1}
          >
            {totalsSummary.map((i, index) => {
              return (
                <Box
                  key={index}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                  flex={1}
                >
                  <span
                    style={{
                      color: "rgba(1,1,1,0.5)",
                      fontSize: 14,
                    }}
                  >
                    {i.title}
                  </span>
                  <span style={{ fontSize: 14 }}>
                    {currencyFormatter.format(i.value)}
                  </span>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      <div style={{ padding: 15 }}>
        <Box mt={1} width={"100%"}>
          {renderActiveOptions()}
        </Box>
      </div>

      {/* <Box mt={1} width={"100%"}>
        {renderActiveOptions()}
      </Box> */}

      <Box display={"flex"} justifyContent={"flex-end"} p={1} gap={1}>
        <Button
          sx={{
            border: "1px solid #FF6262",
            color: "#FF6262",
            cursor: "pointer",
            background: "#FFF",
            "&:hover": {
              border: "1px solid #FF6262",
            },
            p: 0.62,
            height: "100%",
          }}
          variant="outlined"
          onClick={removeSupplier}
          disabled={false}
        >
          Remover fornecedor
        </Button>
        <ActionSelect
          label="Adicionar tipo de serviço"
          actionOptions={generateFilteredOptions(
            BudgetItemTypeEnum,
            activeService?.itemTypeList?.[activeCalcOption]
          )}
          triggerAction={addOption}
          disabled={false}
          applyTabActionFilters={false}
        />
      </Box>
    </Box>
  );
};

export default ServiceSupplier;
